import { asyncCallRoutePost } from '../helpers/api';
import { AddCustomerForm } from './AddCustomerForm';
import { OrderTitleForm } from './OrderTitleForm';

/**
 * Class for handling all custom functionality on view order page (/my-account/view-order/123/)
 */
export class ViewOrderPage {
	constructor( element ) {
		this.element = element;

		this.orderTitleForm = new OrderTitleForm( this.element.querySelector( '.js-qala-b2b-order-title' ) );
		this.footerElement = this.element.querySelector( '.js-qala-b2b-order-footer' );

		// This can happen if order isn't editable.
		if ( ! this.footerElement ) {
			return;
		}

		this.addCustomerForm = this.element.querySelector( '.js-qala-b2b-add-customer' );

		this.hasAddCustomerForm = false;

		// This happens if the add customer form isn't on the page.
		if ( this.addCustomerForm ) {
			this.addCustomerForm = new AddCustomerForm( this.addCustomerForm );
			this.hasAddCustomerForm = true;
		}

		this.sendCustomerEmailButton = this.element.querySelector( '.js-qala-b2b-send-customer-email-button' );
		this.noticeElement = this.element.querySelector( '.js-qala-b2b-order-footer-notice' );
		this.orderId = this.footerElement.getAttribute( 'data-order-id' );

		this.orderUpdatingClass = 'qala-b2b-order-items-updating';
		this.orderItemsTable = this.element.querySelector( '.js-qala-b2b-order-items-table' );
		this.orderItemsItemCount = this.element.querySelector( '.js-qala-b2b-order-items-item-count' );
		this.orderItemsItemProductTotal = this.element.querySelectorAll( '.js-qala-b2b-order-items-table-product-total' );
		this.orderItemsNoItemsNotice = this.element.querySelector( '.js-qala-b2b-order-items-no-items-notice' );
		this.orderItemsTotal = this.element.querySelector( '.js-qala-b2b-order-items-total' );

		this.removeOrderItemButtons = this.element.querySelectorAll( '.js-qala-b2b-order-item-remove' );
		this.updateOrderItemQuantitySelect = this.element.querySelectorAll( '.js-qala-b2b-order-item-update-quantity' );
		this.fixedOrderItemQuantity = this.element.querySelectorAll( '.js-qala-b2b-order-item-fixed-quantity' );

		this.orderItemsNoticeElement = this.element.querySelector( '.js-qala-b2b-order-items-notice' );

		this.shippingAddress = this.element.querySelector( '.woocommerce-column--shipping-address address' );
		this.shippingAddressEditable = false;
		this.editShippingAddressLinkContainer = null;
		this.editShippingAddressElement = this.element.querySelector( '.js-qala-b2b-edit-shipping-address' );
		this.editShippingAddressNoticeElement = this.element.querySelector( '.js-qala-b2b-edit-shipping-address-notice' );
		this.editShippingAddressElementSubmit = this.element.querySelector( '.js-qala-b2b-edit-shipping-address-submit' );
		this.editShippingAddressEditText = window.QalaB2bWholesale.text.edit;
		this.editShippingAddressSaveAddressText = window.QalaB2bWholesale.text.saveAddress;
		this.editShippingAddressSavingAddressText = window.QalaB2bWholesale.text.savingAddress;
		this.editShippingAddressSavedText = window.QalaB2bWholesale.text.addressSaved;

		this.shippingMethodElement = this.element.querySelector( '.js-qala-b2b-order-shipping-method' );
		this.shippingMethodElementItems = this.element.querySelectorAll( '.js-qala-b2b-order-shipping-method-item' );
		this.shippingMethodNoticeElement = this.element.querySelector( '.js-qala-b2b-edit-shipping-method-notice' );

		this.customItemsElement = this.element.querySelector( '.js-qala-b2b-order-custom-items' );
		this.customItemsElementList = this.element.querySelector( '.js-qala-b2b-order-custom-items__list' );
		this.customItemsElementToggleAddFormButton = this.element.querySelector( '.js-qala-b2b-order-custom-items__toggle-add-form' );
		this.customItemsElementAddForm = this.element.querySelector( '.js-qala-b2b-order-custom-items__add-item' );
		this.customItemsElementSaveItemDescription = this.element.querySelector( '.js-qala-b2b-order-custom-items__description-input input' );
		this.customItemsElementSaveItemPrice = this.element.querySelector( '.js-qala-b2b-order-custom-items__price-input input' );
		this.customItemsElementSaveItemButton = this.element.querySelector( '.js-qala-b2b-order-custom-items__save-button button' );
		this.customItemsElementSaveItemButtonsRemove = this.element.querySelectorAll( '.js-qala-b2b-order-custom-items__remove button' );
		this.customItemsElementSaveItemText = window.QalaB2bWholesale.text.saveCustomItem;
		this.customItemsElementSavingItemText = window.QalaB2bWholesale.text.savingCustomItem;
		this.customItemsNoticeElement = this.element.querySelector( '.js-qala-b2b-order-custom-items-notice' );

		this.routeUrl = window.QalaB2bWholesale.updateOrderRouteObj.routeUrl;
		this._wpnonce = window.QalaB2bWholesale.updateOrderRouteObj._wpnonce;

		this.sendEmailRouteUrl = window.QalaB2bWholesale.sendCustomerEmailRouteObj.routeUrl;
		this._wpnonceSendEmail = window.QalaB2bWholesale.sendCustomerEmailRouteObj._wpnonce;

		this.expandProposalHiddenRows = this.element.querySelectorAll( '.qala-b2b-order-items-table__row--hidden' );
		this.expandProposalButtons = this.element.querySelectorAll( '.qala-b2b-order-items-table__col-actions-expand' );
	}

	/**
	 * Initialize the view order page with all needed callbacks
	 *
	 * @return {void}
	 */
	init() {
		if ( this.hasAddCustomerForm ) {
			this.addCustomerForm.init();
		}

		this.setupSendCustomerEmailButton();
		this.setupRemoveOrderItemButton();
		this.setupUpdateOrderItemQuantity();
		this.setupShippingAddressEditing();
		this.setupShippingMethodEditing();
		this.setupCustomItemsEditing();
		this.setupHiddenProposalView();

		this.orderTitleForm.init();
	}

	/**
	 * Setup the save order button.
	 *
	 * @return {void}
	 */
	setupSendCustomerEmailButton() {
		if ( ! this.sendCustomerEmailButton ) {
			return;
		}

		this.sendCustomerEmailButton.addEventListener( 'click', this.sendCustomerEmail.bind( this ) );
	}

	/**
	 * Setup the remove order item button.
	 *
	 * @return {void}
	 */
	setupRemoveOrderItemButton() {
		if ( ! this.removeOrderItemButtons ) {
			return;
		}

		this.removeOrderItemButtons.forEach( button => {
			button.addEventListener( 'click', this.removeOrderItem.bind( this ) );
		} );
	}

	/**
	 * Setup the update order item quantity select.
	 *
	 * @return {void}
	 */
	setupUpdateOrderItemQuantity() {
		if ( ! this.updateOrderItemQuantitySelect ) {
			return;
		}

		this.updateOrderItemQuantitySelect.forEach( select => {
			select.addEventListener( 'change', this.updateOrderItemQuantity.bind( this ) );
		} );
	}

	/**
	 * Setup the shipping address editing.
	 *
	 * @return {void}
	 */
	setupShippingAddressEditing() {
		if ( ! this.editShippingAddressElement ) {
			return;
		}

		this.shippingAddressEditable = this.editShippingAddressElement.dataset.isAddressEditable === '1';
		this.addEditShippingAddressButton();
		this.editShippingAddressElementSubmit.addEventListener( 'click', this.updateShippingAddress.bind( this ) );
	}

	/**
	 * Setup the shipping method editing.
	 *
	 * @return {void}
	 */
	setupShippingMethodEditing() {
		if ( ! this.shippingMethodElement ) {
			return;
		}

		this.shippingMethodElementItems.forEach( item => {
			item.addEventListener( 'click', this.updateShippingMethod.bind( this ) );
		} );
	}

	setupCustomItemsEditing() {
		if ( ! this.customItemsElement ) {
			return;
		}

		this.customItemsElementAddForm.style.display = 'none';
		this.customItemsElementToggleAddFormButton.addEventListener( 'click', this.toggleCustomItemsAddForm.bind( this ) );
		this.customItemsElementSaveItemButton.addEventListener( 'click', this.saveCustomItem.bind( this ) );

		this.customItemsElementList.addEventListener( 'click', event => {
			const button = event.target.closest( '.js-qala-b2b-order-custom-items__remove button' );

			if ( button ) {
				const customItemId = button.getAttribute( 'data-custom-item-id' );
				this.removeCustomItem( customItemId );
			}
		} );
	}

	/**
	 * Activate the Proposal Expand buttons
	 *
	 * @return {void}
	 */
	setupHiddenProposalView() {
		this.expandProposalButtons?.forEach( expandButton => {
			expandButton.addEventListener( 'click', this.toggleExpandProposalHiddenRows.bind( this ) );
			expandButton.removeAttribute( 'disabled' );
		} );
	}

	/**
	 * Update the shipping method.
	 *
	 * @param {Event} event
	 * @return {void}
	 */
	async updateShippingMethod( event ) {
		this.shippingMethodStartValidating();
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				shipping_method: event.target.value,
			} );
			this.shippingMethodProcessResponseSuccess( response );
			window.location.reload();
		} catch ( e ) {
			this.shippingMethodProcessResponseError( e );
		}
		this.shippingMethodEndValidating();
	}

	/**
	 * Add edit shipping address button.
	 *
	 * @return {void}
	 */
	addEditShippingAddressButton() {
		const editShippingAddressLink = document.createElement( 'button' );
		editShippingAddressLink.classList.add( 'btn-type-5' );
		editShippingAddressLink.classList.add( 'qala-b2b-order-edit-shipping-address__edit-button' );

		editShippingAddressLink.innerHTML = this.editShippingAddressEditText;
		editShippingAddressLink.addEventListener( 'click', () => {
			jQuery( this.editShippingAddressElement ).slideToggle();
			this.clearShippingAddressNotice();
		} );

		const editShippingAddressLinkContainer = document.createElement( 'div' );
		editShippingAddressLinkContainer.appendChild( editShippingAddressLink );
		this.editShippingAddressLinkContainer = editShippingAddressLinkContainer;
		this.toggleEditShippingAddressButton( this.shippingAddressEditable );

		const shippingAddressColumn = document.querySelector( '.woocommerce-column--shipping-address address' );
		if ( shippingAddressColumn ) {
			shippingAddressColumn.appendChild( editShippingAddressLinkContainer );
		}
	}

	toggleEditShippingAddressButton( show ) {
		this.editShippingAddressLinkContainer.hidden = ! show;
	}

	/**
	 * Update the shipping address.
	 *
	 * @return {void}
	 */
	async updateShippingAddress() {
		const shippingAddressFields = this.getShippingAddressFields( this.editShippingAddressElement );

		this.shippingAddressStartValidating();
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				shipping_address: shippingAddressFields,
			} );

			this.shippingAddressProcessResponseSuccess( response );
		} catch ( e ) {
			this.shippingAddressProcessResponseError( e );
		}
		this.shippingAddressEndValidating();
	}

	/**
	 * Get the shipping address fields from the form.
	 *
	 * @param {Element} element
	 * @return {Object} Shipping address fields.
	 */
	getShippingAddressFields( element ) {
		const fields = element.querySelectorAll( '.form-row input, .form-row select' );
		const data = {};

		fields.forEach( field => {
			const name = field.name.replace( 'shipping_', '' );
			data[ name ] = field.value;
		} );

		return data;
	}

	/**
	 * Process the response from the server.
	 *
	 * @param {Response} response
	 * @return {void}
	 */
	shippingAddressProcessResponseSuccess( response ) {
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.shippingAddress.innerHTML = response.data.formatted_shipping_address;
			this.setShippingAddressSuccessNotice( this.editShippingAddressSavedText );
			jQuery( this.editShippingAddressElement ).slideUp();
			this.addEditShippingAddressButton();
		} else {
			this.setShippingAddressErrorNotice( response.message );
		}
	}

	/**
	 * Process the error response from the server.
	 *
	 * @param {Error} e
	 * @return {void}
	 */
	shippingAddressProcessResponseError( e ) {
		this.setShippingAddressErrorNotice( e.message );
	}

	/**
	 * Start validating the shipping address.
	 *
	 * @return {void}
	 */
	shippingAddressStartValidating() {
		this.editShippingAddressElementSubmit.disabled = true;
		this.editShippingAddressElementSubmit.innerHTML = this.editShippingAddressSavingAddressText;
	}

	/**
	 * End validating the shipping address.
	 *
	 * @return {void}
	 */
	shippingAddressEndValidating() {
		this.editShippingAddressElementSubmit.disabled = false;
		this.editShippingAddressElementSubmit.innerHTML = this.editShippingAddressSaveAddressText;
	}

	/**
	 * Toggle the custom items add form.
	 *
	 * @return {void}
	 */
	toggleCustomItemsAddForm() {
		jQuery( this.customItemsElementAddForm ).slideToggle();
	}

	/**
	 *
	 *
	 * @param {Element} element
	 * @return {void}
	 */
	toggleExpandProposalHiddenRows( element ) {
		const button = element.target.closest( '.qala-b2b-order-items-table__col-actions-expand' );
		const parent = button.closest( '.qala-b2b-order-items-table__row' );

		if ( ! parent ) {
			return;
		}

		let sibling = parent.nextElementSibling;

		// Toggle --hidden class for the siblings with the --can-hide class next to the parent
		while ( sibling && sibling.classList.contains( 'qala-b2b-order-items-table__row--can-hide' ) ) {
			sibling.classList.toggle( 'qala-b2b-order-items-table__row--hidden' );
			sibling = sibling.nextElementSibling;
		}

		// Toggle button active class (for chevron icon state)
		button.classList.toggle( 'qala-b2b-order-items-table__col-actions-expand--active' );

		// Toggle button text
		const buttonSpan = button.querySelector( 'span' );
		buttonSpan.textContent = buttonSpan.textContent.trim() === button.dataset.labelExpand
			? button.dataset.labelCollapse
			: button.dataset.labelExpand;
	}

	/**
	 * Save the custom item.
	 */
	async saveCustomItem() {
		const description = this.customItemsElementSaveItemDescription.value;
		const price = this.customItemsElementSaveItemPrice.value;

		this.customItemStartValidating();
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				custom_item_add: {
					description,
					price,
				},
			} );
			this.customItemProcessResponseSuccess( response );
			this.customItemsElementSaveItemDescription.value = '';
			this.customItemsElementSaveItemPrice.value = '';
		} catch ( e ) {
			this.customItemProcessResponseError( e );
		}
		this.customItemEndValidating();
	}

	/**
	 * Remove the custom item.
	 *
	 * @param {string} customItemId
	 */
	async removeCustomItem( customItemId ) {
		this.customItemRemoveStartValidating( customItemId );
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				custom_item_remove: customItemId,
			} );
			this.customItemProcessResponseSuccess( response );
		} catch ( e ) {
			this.customItemProcessResponseError( e );
		}
		this.customItemRemoveEndValidating( customItemId );
	}

	/**
	 * Save or update order items.
	 *
	 * @param {Object} orderData
	 * @return {boolean} Whether the request was successful or not.
	 */
	async saveOrderItems( orderData ) {
		try {
			const response = await asyncCallRoutePost( this.routeUrl, this._wpnonce, {
				order_id: this.orderId,
				...orderData,
			} );
			return this.processOrderItemsResponse( response );
		} catch ( e ) {
			this.processOrderItemsErrorResponse( e );
			return false;
		}
	}

	/**
	 * Send the request for saving order.
	 *
	 * @param {Event} event
	 */
	async sendCustomerEmail( event ) {
		event.preventDefault();

		this.startValidating();
		try {
			const response = await asyncCallRoutePost( this.sendEmailRouteUrl, this._wpnonceSendEmail, {
				order_id: this.orderId,
			} );
			this.processResponse( response );
		} catch ( e ) {
			this.processErrorResponse( e );
		}

		this.endValidating();
	}

	/**
	 * Add order item to the list of items to remove.
	 *
	 * @param {Event} event
	 */
	async removeOrderItem( event ) {
		const itemId = +event.target.getAttribute( 'data-item-id' );

		if ( ! itemId ) {
			return;
		}

		const orderItemRow = event.target.closest( 'tr' );
		orderItemRow.classList.add( this.orderUpdatingClass );

		const isSuccess = await this.saveOrderItems( { items_to_remove: [ itemId ] } );

		orderItemRow.classList.remove( this.orderUpdatingClass );

		if ( ! isSuccess ) {
			return;
		}

		orderItemRow.remove();

		// If this item was composite container, remove the composited children.
		this.element.querySelectorAll( `[data-composite-container="${ itemId }"]` ).forEach( compositedElement => {
			// Check if item is a bundle container, if so remove all it's children first.
			if ( compositedElement.hasAttribute( 'data-bundle' ) ) {
				const bundleId = compositedElement.getAttribute( 'data-bundle' );
				this.element.querySelectorAll( `[data-bundle-container="${ bundleId }"]` ).forEach( bundledElement => bundledElement.remove() );
			}

			compositedElement.remove();
		} );
	}

	/**
	 * Update the order item quantity in the list of items to update.
	 *
	 * @param {Event} event
	 * @return {void}
	 */
	async updateOrderItemQuantity( event ) {
		const itemId = +event.target.getAttribute( 'data-item-id' );

		if ( ! itemId ) {
			return;
		}

		const orderItemRow = event.target.closest( 'tr' );
		orderItemRow.classList.add( this.orderUpdatingClass );

		await this.saveOrderItems( { items_quantity_update: {
			[ itemId ]: +event.target.value,
		} } );

		orderItemRow.classList.remove( this.orderUpdatingClass );
	}

	/**
	 * Update the order items table with new data.
	 *
	 * @param {Object} data
	 */
	renderTableData( data ) {
		if ( data.total_items === 0 ) {
			this.orderItemsTable.remove();
			this.orderItemsNoItemsNotice.hidden = false;
		}

		this.orderItemsItemCount.innerHTML = data.total_items_text;
		this.orderItemsTotal.innerHTML = data.order_total;

		this.orderItemsItemProductTotal.forEach( itemProductTotal => {
			const itemId = +itemProductTotal.getAttribute( 'data-item-id' );
			const itemData = data.order_items.find( item => item.item_id === itemId );

			if ( itemData ) {
				itemProductTotal.innerHTML = itemData.total;
			}
		} );

		this.fixedOrderItemQuantity.forEach( itemProductQuantity => {
			const itemId = +itemProductQuantity.getAttribute( 'data-item-id' );
			const itemData = data.order_items.find( item => item.item_id === itemId );

			if ( itemData ) {
				itemProductQuantity.innerHTML = itemData.quantity;
			}
		} );

		this.customItemsElementList.innerHTML = data.custom_order_items.reduce( ( acc, item ) => {
			return acc + `<div class="qala-b2b-order-custom-items__list-item">
				<div class="qala-b2b-order-custom-items__description">${ item.description }</div>
				<div class="qala-b2b-order-custom-items__price">${ item.price }</div>
				<div class="js-qala-b2b-order-custom-items__remove qala-b2b-order-custom-items__remove">
					<button type="button" class="btn-link" data-custom-item-id="${ item.id }">
						<svg xmlns="http://www.w3.org/2000/svg" height="16" width="12" viewBox="0 0 384 512">
							<path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
						</svg>
					</button>
				</div>
			</div>`;
		}, '' );

		this.toggleEditShippingAddressButton( data.is_shipping_address_editable );
	}

	/**
	 * Start the request for validating customer email.
	 *
	 * @return {void}
	 */
	startValidating() {
		this.isValidationInProgress = true;
		this.sendCustomerEmailButton.disabled = true;
		this.clearNotice();
	}

	/**
	 * End the request for validating customer email.
	 *
	 * @return {void}
	 */
	endValidating() {
		this.isValidationInProgress = false;
		this.sendCustomerEmailButton.disabled = false;
	}

	/**
	 * Process the response from the server, assuming everything is ok (no server error)
	 *
	 * @param {Object} response
	 */
	processResponse( response ) {
		this.noticeElement.innerHTML = response.message;
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.setSuccessNotice( response.message );
		} else {
			this.setErrorNotice( response.message );
		}
	}

	/**
	 * Process the error response from the server, happens if there is a server error (500)
	 *
	 * @return {void}
	 */
	processErrorResponse() {
		this.setErrorNotice( window.QalaB2bWholesale.text.unknownError );
	}

	/**
	 * Set the success notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setSuccessNotice( text ) {
		this.noticeElement.hidden = false;
		this.noticeElement.innerHTML = text;
		this.noticeElement.classList.remove( 'woocommerce-error' );
		this.noticeElement.classList.add( 'woocommerce-message' );
		this.hideNoticeAfterDelay( this.noticeElement );
	}

	/**
	 * Set the error notice.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setErrorNotice( text ) {
		this.noticeElement.hidden = false;
		this.noticeElement.innerHTML = text;
		this.noticeElement.classList.remove( 'woocommerce-message' );
		this.noticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.noticeElement );
	}

	/**
	 * Clear the notice.
	 *
	 * @return {void}
	 */
	clearNotice() {
		this.noticeElement.innerHTML = '';
		this.noticeElement.classList.remove( 'woocommerce-message' );
		this.noticeElement.classList.remove( 'woocommerce-error' );
	}

	/**
	 * Set the success notice for shipping address.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setShippingAddressSuccessNotice( text ) {
		this.editShippingAddressNoticeElement.hidden = false;
		this.editShippingAddressNoticeElement.innerHTML = text;
		this.editShippingAddressNoticeElement.classList.remove( 'woocommerce-error' );
		this.editShippingAddressNoticeElement.classList.add( 'woocommerce-message' );
		this.hideNoticeAfterDelay( this.editShippingAddressNoticeElement );
	}

	/**
	 * Set the error notice for shipping address.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setShippingAddressErrorNotice( text ) {
		this.editShippingAddressNoticeElement.hidden = false;
		this.editShippingAddressNoticeElement.innerHTML = text;
		this.editShippingAddressNoticeElement.classList.remove( 'woocommerce-message' );
		this.editShippingAddressNoticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.editShippingAddressNoticeElement );
	}

	/**
	 * Clear the notice for shipping address.
	 *
	 * @return {void}
	 */
	clearShippingAddressNotice() {
		this.editShippingAddressNoticeElement.innerHTML = '';
		this.editShippingAddressNoticeElement.classList.remove( 'woocommerce-message' );
		this.editShippingAddressNoticeElement.classList.remove( 'woocommerce-error' );
	}

	/**
	 * Process the response from the server.
	 *
	 * @param {Object} response
	 * @return {boolean} Whether the response was successful or not.
	 */
	processOrderItemsResponse( response ) {
		this.orderItemsNoticeElement.innerHTML = response.message;
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.renderTableData( response.data );
			this.setOrderItemsSuccessNotice( response.message );
		} else {
			this.setOrderItemsErrorNotice( response.message );
		}

		return isSuccess;
	}

	/**
	 * Process the error response from the server, happens if there is a server error (500)
	 *
	 * @return {void}
	 */
	processOrderItemsErrorResponse() {
		this.setOrderItemsErrorNotice( window.QalaB2bWholesale.text.unknownError );
	}

	/**
	 * Set the order items success notice.
	 *
	 * @param {string} text
	 */
	setOrderItemsSuccessNotice( text ) {
		this.orderItemsNoticeElement.hidden = false;
		this.orderItemsNoticeElement.innerHTML = text;
		this.orderItemsNoticeElement.classList.remove( 'woocommerce-error' );
		this.orderItemsNoticeElement.classList.add( 'woocommerce-message' );
		this.hideNoticeAfterDelay( this.orderItemsNoticeElement );
	}

	/**
	 * Set the order items error notice.
	 *
	 * @param {string} text
	 */
	setOrderItemsErrorNotice( text ) {
		this.orderItemsNoticeElement.hidden = false;
		this.orderItemsNoticeElement.innerHTML = text;
		this.orderItemsNoticeElement.classList.remove( 'woocommerce-message' );
		this.orderItemsNoticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.orderItemsNoticeElement );
	}

	/**
	 * Start validating shipping method update.
	 *
	 * @return {void}
	 */
	shippingMethodStartValidating() {
		this.shippingMethodElementItems.forEach( item => {
			item.disabled = true;
		} );
	}

	/**
	 * End validating shipping method update.
	 */
	shippingMethodEndValidating() {
		this.shippingMethodElementItems.forEach( item => {
			item.disabled = false;
		} );
	}

	/**
	 * Process the response from the server.
	 *
	 * @param {Response} response
	 */
	shippingMethodProcessResponseSuccess( response ) {
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.shippingAddressEditable = response.data.is_shipping_address_editable;
			this.setShippingMethodSuccessNotice( response.message );
			this.renderTableData( response.data );
		} else {
			this.setShippingMethodErrorNotice( response.message );
		}
	}

	/**
	 * Process the error response from the server.
	 *
	 * @param {Error} error
	 */
	shippingMethodProcessResponseError( error ) {
		this.setShippingMethodErrorNotice( error.message );
	}

	/**
	 * Set the success notice for shipping method.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setShippingMethodSuccessNotice( text ) {
		this.shippingMethodNoticeElement.hidden = false;
		this.shippingMethodNoticeElement.innerHTML = text;
		this.shippingMethodNoticeElement.classList.remove( 'woocommerce-error' );
		this.shippingMethodNoticeElement.classList.add( 'woocommerce-message' );
		this.hideNoticeAfterDelay( this.shippingMethodNoticeElement );
	}

	/**
	 * Set the error notice for shipping method.
	 *
	 * @param {string} text Text to set.
	 * @return {void}
	 */
	setShippingMethodErrorNotice( text ) {
		this.shippingMethodNoticeElement.hidden = false;
		this.shippingMethodNoticeElement.innerHTML = text;
		this.shippingMethodNoticeElement.classList.remove( 'woocommerce-message' );
		this.shippingMethodNoticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.shippingMethodNoticeElement );
	}

	/**
	 * Start validating custom item.
	 *
	 * @return {void}
	 */
	customItemStartValidating() {
		this.customItemsElementSaveItemButton.disabled = true;
		this.customItemsElementSaveItemButton.innerHTML = this.customItemsElementSavingItemText;
	}

	/**
	 * End validating custom item.
	 *
	 * @return {void}
	 */
	customItemEndValidating() {
		this.customItemsElementSaveItemButton.disabled = false;
		this.customItemsElementSaveItemButton.innerHTML = this.customItemsElementSaveItemText;
	}

	customItemProcessResponseSuccess( response ) {
		let isSuccess = false;

		if ( response?.data?.status && response.data.status === 200 ) {
			isSuccess = true;
		}

		if ( isSuccess ) {
			this.renderTableData( response.data );
			this.customItemsElementAddForm.style.display = 'none';
		} else {
			this.setCustomItemErrorNotice( response.message );
		}
	}

	/**
	 * Process the error response from the server.
	 *
	 * @param {Error} error
	 */
	customItemProcessResponseError( error ) {
		this.setCustomItemErrorNotice( error.message );
	}

	/**
	 * Start validating custom item remove.
	 *
	 * @param {string} customItemId
	 */
	customItemRemoveStartValidating( customItemId ) {
		const button = document.querySelector( `[data-custom-item-id="${ customItemId }"]` );
		button.disabled = true;
		button.closest( '.qala-b2b-order-custom-items__list-item' ).style.opacity = 0.3;
	}

	/**
	 * End validating custom item remove.
	 *
	 * @param {string} customItemId
	 */
	customItemRemoveEndValidating( customItemId ) {
		const button = document.querySelector( `[data-custom-item-id="${ customItemId }"]` );

		if ( button ) {
			button.disabled = false;
			button.closest( '.qala-b2b-order-custom-items__list-item' ).style.opacity = 1;
		}
	}

	/**
	 * Set the error notice for custom item.
	 *
	 * @param {string} text Text to set.
	 */
	setCustomItemErrorNotice( text ) {
		this.customItemsNoticeElement.hidden = false;
		this.customItemsNoticeElement.innerHTML = text;
		this.customItemsNoticeElement.classList.remove( 'woocommerce-message' );
		this.customItemsNoticeElement.classList.add( 'woocommerce-error' );
		this.hideNoticeAfterDelay( this.customItemsNoticeElement );
	}

	/**
	 * Hide the notice after a delay.
	 *
	 * @param {Element} element
	 * @return {void}
	 */
	hideNoticeAfterDelay( element ) {
		setTimeout( () => {
			element.hidden = true;
		}, 5000 );
	}
}
